<script lang="ts" setup>
import {computed} from 'vue';
import {useI18n} from 'vue-i18n';
import {QBtnDropdown, QItem, QItemSection} from 'quasar';
import {PeriodFilter} from '@/ts/types/component/period-filter';

const props = withDefaults(defineProps<{
  availableFilters?: PeriodFilter[];
}>(), {
  availableFilters: () => Object.values(PeriodFilter),
});

const selectedPeriod = defineModel<PeriodFilter>({default: PeriodFilter.LAST_3_MONTHS});

const {t: $t} = useI18n();

interface FilterOption {
  label: string;
  value: PeriodFilter;
}

const allFilterOptions: Record<PeriodFilter, () => string> = {
  [PeriodFilter.TODAY]: () => $t('today'),
  [PeriodFilter.LAST_WEEK]: () => $t('last_week'),
  [PeriodFilter.LAST_MONTH]: () => $t('last_month'),
  [PeriodFilter.CURRENT_WEEK]: () => $t('current_week'),
  [PeriodFilter.CURRENT_MONTH]: () => $t('current_month'),
  [PeriodFilter.LAST_3_MONTHS]: () => $t('last_3_months'),
  [PeriodFilter.LAST_6_MONTHS]: () => $t('last_6_months'),
  [PeriodFilter.LAST_YEAR]: () => $t('last_year'),
  [PeriodFilter.ALL]: () => $t('all_entries'),
};

const filterOptions = computed<FilterOption[]>(() => props.availableFilters.map(filter => ({
  label: allFilterOptions[filter](),
  value: filter,
})));

const currentFilter = computed(() =>
  filterOptions.value.find(option => option.value === selectedPeriod.value) ?? filterOptions.value[0]);

function selectFilter(newFilter: PeriodFilter): void {
  if (newFilter !== selectedPeriod.value) {
    selectedPeriod.value = newFilter;
  }
}
</script>

<template>
  <q-btn-dropdown
    class="period-filter"
    flat
    dense
  >
    <template #label>
      <div class="selected-filter text-body">
        <span class="label">{{ currentFilter.label }}</span>
      </div>
    </template>

    <!-- Quasar 2.28 dropped the default role on QMenu, so the single-select list declares it -->
    <q-list role="listbox">
      <q-item
        v-for="option in filterOptions"
        :key="option.value"
        v-close-popup
        clickable
        role="option"
        :aria-selected="option.value === selectedPeriod"
        :class="{'selected-option': option.value === selectedPeriod}"
        @click="selectFilter(option.value)"
      >
        <q-item-section>
          {{ option.label }}
        </q-item-section>
      </q-item>
    </q-list>
  </q-btn-dropdown>
</template>

<style lang="scss" scoped>
.period-filter {
  @include focus-visible;
  padding: var(--Spacing-spacing-1) var(--Spacing-spacing-3);
  border-radius: var(--button-border-radius);
}

.selected-filter {
  display: flex;
  align-items: center;
}

.label {
  @include font-body-sm-bold;
  color: var(--Neutral-UI-neutral-ui-100);
}

.selected-option {
  background-color: var(--Secondary-secondary-color-1);
  color: var(--Neutral-UI-neutral-ui-100);
}
</style>
